/**
 * 351 (#948): the finance lists' shapes, their words, and their CSV. The CSV is exactly the listed rows, with amounts
 * in ringgit and times in Malaysia time whatever the device's zone (Malaysia has no daylight saving: a fixed +8 h).
 */
export interface Person {
  id: string;
  name: string;
  email?: string;
}
export interface PaymentRow {
  id: string;
  date: string | null;
  paid_at: string | null;
  created_at: string | null;
  booking_id: string;
  customer: (Person & { email: string }) | null;
  kind: string;
  provider: string;
  channel: string | null;
  amount_minor: number;
  refunded_minor: number;
  status: string;
  mismatch: boolean;
  provider_transaction_id: string | null;
}
export interface PaymentList {
  items: PaymentRow[];
  count: number;
  truncated: boolean;
  totals: { received_minor: number; refunded_minor: number; net_minor: number };
}
export interface RefundRow {
  id: string;
  created_at: string | null;
  payment_id: string;
  booking_id: string;
  customer: (Person & { email: string }) | null;
  amount_minor: number;
  status: string;
  reason: string | null;
  approved_by: Person | null;
  approved_at: string | null;
  processed_at: string | null;
}
export interface RefundList {
  items: RefundRow[];
  count: number;
  truncated: boolean;
  totals: { refunded_minor: number };
}

const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;
export const ringgit = (minor: number) => (minor / 100).toFixed(2);
/** The booking reference the panel shows everywhere: the last 8 characters of its id. */
export const shortRef = (id: string) => id.slice(-8);
/** `YYYY-MM-DD HH:mm` in Malaysia time; empty for none. */
export function mytDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(new Date(iso).getTime() + MYT_OFFSET_MS)
    .toISOString()
    .slice(0, 16)
    .replace('T', ' ');
}

const words = (s: string) => {
  const w = s.replace(/_/g, ' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
};
export const STATUS_WORD = (s: string) => words(s);
export const PROVIDER_WORD: Record<string, string> = {
  bayarcash: 'Bayarcash',
  gift_certificate: 'Gift certificate',
  // 421 (#1528): a late extension paid in cash, by transfer or by CARii's QR; the method is the channel.
  outside: 'Outside the app',
};
export const KIND_WORD: Record<string, string> = { booking: 'Booking', extension: 'Extension' };

const quote = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
/** 351: CSV text, every cell quoted when it needs to be (also used by 361's trips export). */
export const csv = (lines: string[][]) =>
  `${lines.map((l) => l.map(quote).join(',')).join('\r\n')}\r\n`;

export function paymentsToCsv(list: PaymentList): string {
  return csv([
    [
      'Date (MYT)',
      'Booking',
      'Customer',
      'Email',
      'Kind',
      'Provider',
      'Channel',
      'Amount (RM)',
      'Refunded (RM)',
      'Status',
      'Mismatch',
      'Transaction',
    ],
    ...list.items.map((p) => [
      mytDateTime(p.date),
      shortRef(p.booking_id),
      p.customer?.name ?? '—',
      p.customer?.email ?? '',
      KIND_WORD[p.kind] ?? words(p.kind),
      PROVIDER_WORD[p.provider] ?? words(p.provider),
      p.channel ?? '',
      ringgit(p.amount_minor),
      ringgit(p.refunded_minor),
      STATUS_WORD(p.status),
      p.mismatch ? 'Yes' : '',
      p.provider_transaction_id ?? '',
    ]),
    [
      'Totals',
      '',
      '',
      '',
      '',
      '',
      '',
      `Received ${ringgit(list.totals.received_minor)}`,
      `Refunded ${ringgit(list.totals.refunded_minor)}`,
      `Net ${ringgit(list.totals.net_minor)}`,
      '',
      '',
    ],
  ]);
}

export function refundsToCsv(list: RefundList): string {
  return csv([
    [
      'Logged (MYT)',
      'Booking',
      'Customer',
      'Email',
      'Amount (RM)',
      'Status',
      'Reason',
      'Approved by',
      'Approved (MYT)',
      'Processed (MYT)',
    ],
    ...list.items.map((r) => [
      mytDateTime(r.created_at),
      shortRef(r.booking_id),
      r.customer?.name ?? '—',
      r.customer?.email ?? '',
      ringgit(r.amount_minor),
      STATUS_WORD(r.status),
      r.reason ?? '',
      r.approved_by?.name ?? '',
      mytDateTime(r.approved_at),
      mytDateTime(r.processed_at),
    ]),
    ['Total refunded', '', '', '', ringgit(list.totals.refunded_minor), '', '', '', '', ''],
  ]);
}
